import React, { useState, useEffect, useContext } from "react";
import { Container, Button } from "react-bootstrap";
import {
  IconCrown,
  IconRefresh,
  IconAlertTriangle,
  IconSparkles,
  IconX,
} from "@tabler/icons-react";
import Swal from "sweetalert2";
import UserContext from "../../contexts/UserContext";
import PlanServices from "../../services/PlanServices";
import CustomerSubscriptionServices from "../../services/CustomerSubscriptionServices";

// Componentes internos
import CurrentPlanCard from "./components/CurrentPlanCard";
import AvailablePlansGrid from "./components/AvailablePlansGrid";
import AddonsSection from "./components/AddonsSection";
import UpgradePlanModal from "./modals/UpgradePlanModal";
import SubscribePlanModal from "./modals/SubscribePlanModal";
import ContractAddonModal from "./modals/ContractAddonModal";
import CancelPlanModal from "./modals/CancelPlanModal";

// Estilos
import "./MyPlan.css";

// =============================================== //
// SKELETON LOADERS                               //
// =============================================== //
const HeroSkeleton = () => (
  <div className="skeleton-hero myplan-skeleton" />
);

const PlansGridSkeleton = () => (
  <div className="plans-grid">
    {[1, 2, 3].map((i) => (
      <div key={i} className="skeleton-card myplan-skeleton" style={{ animationDelay: `${i * 0.1}s` }} />
    ))}
  </div>
);

// =============================================== //
// COMPONENTE PRINCIPAL                           //
// =============================================== //
const MyPlanScreen = () => {
  const { user, isOrganization } = useContext(UserContext);

  // Estados de dados
  const [currentPlan, setCurrentPlan] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [planFeatures, setPlanFeatures] = useState([]);

  // Estados de loading
  const [loading, setLoading] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Estados de modais
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlanForUpgrade, setSelectedPlanForUpgrade] = useState(null);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [selectedPlanForSubscribe, setSelectedPlanForSubscribe] = useState(null);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Estados de erro
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (showRefreshing = false) => {
    if (showRefreshing) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      await Promise.all([
        loadCurrentPlan(),
        loadAvailablePlans()
      ]);
    } catch (err) {
      console.error("Erro ao carregar dados:", err);
      setError("Não foi possível carregar os dados do plano. Tente novamente.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadCurrentPlan = async () => {
    try {
      const response = await PlanServices.getCurrentPlan();
      if (response.data) {
        const { plan, features } = response.data;
        const mappedStatus = PlanServices.mapPlanStatus(plan.status);
        setCurrentPlan({
          id: plan.id,
          name: plan.name,
          status: mappedStatus,
          started_at: plan.aquisition_date,
          expiration_date: plan.expiration_date,
          usage_type: plan.usage_type,
          is_personal: plan.is_personal,
          monthly_value: plan.monthly_value,
          is_trial: !!plan.is_trial,
          trial_expired: !!plan.trial_expired,
          is_expired: !!plan.is_expired,
          days_remaining: typeof plan.days_remaining === 'number' ? plan.days_remaining : null,
        });

        setPlanFeatures(features || []);
      }
    } catch (err) {
      console.warn("API de plano atual não disponível, buscando assinatura ativa");
      try {
        const subscriptionResponse = await CustomerSubscriptionServices.listActiveSubscriptions({
          type: "subaccount"
        });

        if (subscriptionResponse.data?.results?.length > 0) {
          const activeSubscription = subscriptionResponse.data.results[0];
          setCurrentSubscription(activeSubscription);

          const planId = identifyPlanFromSubscription(activeSubscription);
          const planData = PlanServices.getPlanById(planId);

          if (planData) {
            setCurrentPlan({
              ...planData,
              status: activeSubscription.status_display === "ACTIVE" ? "ACTIVE" : "INACTIVE",
              price: activeSubscription.value,
              recurrency: activeSubscription.recurrency,
              next_billing_date: activeSubscription.next_due_date,
              started_at: activeSubscription.created_at
            });
          }
        } else {
          const defaultPlan = PlanServices.getPlanById("ondemand");
          setCurrentPlan({
            ...defaultPlan,
            status: "NO_PLAN"
          });
        }
      } catch (subError) {
        console.error("Erro ao buscar assinatura:", subError);
        const defaultPlan = PlanServices.getPlanById("ondemand");
        setCurrentPlan({
          ...defaultPlan,
          status: "NO_PLAN"
        });
      }
    }
  };

  const loadAvailablePlans = async () => {
    setLoadingPlans(true);
    try {
      const response = await PlanServices.getAvailablePlans();
      if (response.data?.plans) {
        setAvailablePlans(response.data.plans);
      } else {
        setAvailablePlans(Object.values(PlanServices.PLAN_RESOURCES));
      }
    } catch (err) {
      console.warn("Usando dados estáticos de planos");
      setAvailablePlans(Object.values(PlanServices.PLAN_RESOURCES));
    } finally {
      setLoadingPlans(false);
    }
  };

  const identifyPlanFromSubscription = (subscription) => {
    const description = subscription.description?.toLowerCase() || "";
    const value = parseFloat(subscription.value) || 0;

    if (description.includes("business") || value >= 200) {
      return "business";
    }
    return "ondemand";
  };

  // Handlers
  const handleRefresh = () => {
    loadData(true);
  };

  const handleSelectPlanForUpgrade = (plan, opts = {}) => {
    // Modo "subscribe": converter trial em plano pago no MESMO plano,
    // ou contratar quando o trial já expirou. Não passa pelas validações
    // de upgrade (mesmo plano / downgrade) pois é fluxo distinto.
    if (opts?.mode === "subscribe") {
      setSelectedPlanForSubscribe(plan);
      setShowSubscribeModal(true);
      return;
    }

    if (!currentPlan || String(plan.id) === String(currentPlan.id)) {
      Swal.fire({
        icon: "info",
        title: "Plano Atual",
        text: "Este já é o seu plano atual.",
        confirmButtonColor: "#3085d6"
      });
      return;
    }

    if (!PlanServices.isPlanUpgrade(currentPlan.id, plan.id)) {
      Swal.fire({
        icon: "warning",
        title: "Downgrade não disponível",
        text: "Não é possível fazer downgrade de plano diretamente. Entre em contato com o suporte.",
        confirmButtonColor: "#3085d6"
      });
      return;
    }

    setSelectedPlanForUpgrade(plan);
    setShowUpgradeModal(true);
  };

  const handleSubscribeModalClose = () => {
    setShowSubscribeModal(false);
    setSelectedPlanForSubscribe(null);
    loadData();
  };

  const handleConfirmUpgrade = async () => {
    // O modal agora processa o upgrade internamente
    // Este callback é chamado quando o usuário fecha a tela de sucesso
    // O reload dos dados é feito pelo handleUpgradeModalClose
  };

  const handleUpgradeModalClose = () => {
    setShowUpgradeModal(false);
    // Se houve upgrade, recarregar os dados
    loadData();
  };

  const handleSelectAddon = (addon) => {
    setSelectedAddon(addon);
    setShowAddonModal(true);
  };

  const handleConfirmAddon = async (addon, quantity) => {
    try {
      Swal.fire({
        title: "Processando...",
        text: "Aguarde enquanto processamos sua contratação.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      await PlanServices.contractAddon(addon.id, quantity);

      Swal.fire({
        icon: "success",
        title: "Recurso Contratado!",
        text: "O recurso adicional foi contratado com sucesso.",
        confirmButtonColor: "#28a745"
      });

      setShowAddonModal(false);
      loadData();
    } catch (err) {
      const errorInfo = PlanServices.processPlanError(err);
      Swal.fire({
        icon: "error",
        title: "Erro na Contratação",
        text: errorInfo.message,
        confirmButtonColor: "#dc3545"
      });
    }
  };

  // =============================================== //
  // RENDER - ERROR STATE                           //
  // =============================================== //
  if (error && !loading) {
    return (
      <Container className="py-4">
        <div className="error-container">
          <IconAlertTriangle className="error-icon" size={48} />
          <h3 className="error-title">Ops! Algo deu errado</h3>
          <p className="error-message">{error}</p>
          <button className="error-button" onClick={handleRefresh}>
            <IconRefresh size={16} />
            Tentar Novamente
          </button>
        </div>
      </Container>
    );
  }

  // =============================================== //
  // RENDER - MAIN                                  //
  // =============================================== //
  return (
    <Container className="py-4">
     

      {/* Seção: Plano Atual */}
      <section className="myplan-section">
        {loading ? (
          <HeroSkeleton />
        ) : (
          <CurrentPlanCard
            plan={currentPlan}
            subscription={currentSubscription}
            features={planFeatures}
          />
        )}
      </section>

      {/* Seção: Recursos Adicionais (planos comerciais: Business legado e WhiteLabel) */}
      {!loading && currentPlan && ["5", "9", "10", "11", "business"].includes(String(currentPlan.id)) && (
        <section className="myplan-section fade-in">
          <AddonsSection
            addons={PlanServices.PLAN_RESOURCES.business.addons}
            onSelectAddon={handleSelectAddon}
          />
        </section>
      )}

      {/* Seção: Planos Disponíveis */}
      <section className="myplan-section">
        {loadingPlans ? (
          <PlansGridSkeleton />
        ) : (
          <AvailablePlansGrid
            plans={availablePlans}
            currentPlan={currentPlan}
            onSelectPlan={handleSelectPlanForUpgrade}
          />
        )}

        {/* Botão de cancelar plano (não disponível em trial — não há assinatura) */}
        {!loading && currentPlan && currentPlan.status === "ACTIVE" && currentPlan.monthly_value > 0 && !currentPlan.is_trial && (
          <div className="text-center mt-4">
            <Button
              variant="danger"
              size="sm"
              className="text-white text-decoration-none"
              onClick={() => setShowCancelModal(true)}
            >
              <IconX size={14} className="me-1" />
              Cancelar meu plano
            </Button>
          </div>
        )}
      </section>

      {/* Modais */}
      <UpgradePlanModal
        show={showUpgradeModal}
        onHide={handleUpgradeModalClose}
        plan={selectedPlanForUpgrade}
        currentPlan={currentPlan}
        onConfirm={handleConfirmUpgrade}
      />

      <SubscribePlanModal
        show={showSubscribeModal}
        onHide={handleSubscribeModalClose}
        plan={selectedPlanForSubscribe}
        currentPlan={currentPlan}
      />

      <ContractAddonModal
        show={showAddonModal}
        onHide={() => setShowAddonModal(false)}
        addon={selectedAddon}
        onConfirm={handleConfirmAddon}
      />

      <CancelPlanModal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        onSuccess={() => {
          setShowCancelModal(false);
          loadData();
        }}
      />
    </Container>
  );
};

export default MyPlanScreen;
